import React from 'react';
import {
    DollarSign,
    TrendingUp,
    TrendingDown,
    Calculator,
    CreditCard,
    Receipt,
    PieChart,
    BarChart3,
    AlertCircle,
    CheckCircle,
    Clock,
    FileText
} from 'lucide-react';
import { Card } from '../../components/ui';

const DashboardAccount: React.FC = () => {
    const financialStats = [
        {
            name: 'Monthly Revenue',
            value: '$45,230',
            change: '+12.5%',
            changeType: 'positive',
            icon: DollarSign,
            color: 'bg-gradient-to-r from-emerald-500 to-emerald-600',
            trend: 'up',
        },
        {
            name: 'Pending Fees',
            value: '$8,450',
            change: '-5.2%',
            changeType: 'negative',
            icon: TrendingDown,
            color: 'bg-gradient-to-r from-red-500 to-red-600',
            trend: 'down',
        },
        {
            name: 'Operating Costs',
            value: '$28,900',
            change: '+3.1%',
            changeType: 'positive',
            icon: Calculator,
            color: 'bg-gradient-to-r from-orange-500 to-orange-600',
            trend: 'up',
        },
        {
            name: 'Net Profit',
            value: '$16,330',
            change: '+18.7%',
            changeType: 'positive',
            icon: TrendingUp,
            color: 'bg-gradient-to-r from-teal-500 to-teal-600',
            trend: 'up',
        },
    ];

    const accountMetrics = [
        {
            name: 'Accounts Receivable',
            value: '$12,500',
            change: '+8.2%',
            changeType: 'positive',
            icon: CreditCard,
            trend: 'up',
        },
        {
            name: 'Accounts Payable',
            value: '$5,200',
            change: '-2.1%',
            changeType: 'negative',
            icon: Receipt,
            trend: 'down',
        },
        {
            name: 'Cash Flow',
            value: '$7,300',
            change: '+15%',
            changeType: 'positive',
            icon: BarChart3,
            trend: 'up',
        },
    ];

    const expenseBreakdown = [
        { category: 'Salaries', amount: 45000, percentage: 45, color: 'bg-emerald-500' },
        { category: 'Utilities', amount: 8000, percentage: 8, color: 'bg-teal-500' },
        { category: 'Maintenance', amount: 12000, percentage: 12, color: 'bg-cyan-500' },
        { category: 'Supplies', amount: 15000, percentage: 15, color: 'bg-orange-500' },
        { category: 'Transport', amount: 10000, percentage: 10, color: 'bg-purple-500' },
        { category: 'Others', amount: 10000, percentage: 10, color: 'bg-pink-500' },
    ];

    const recentTransactions = [
        {
            id: 1,
            type: 'income',
            description: 'Monthly fees collected from Grade 10',
            amount: '$2,500',
            time: '2 hours ago',
        },
        {
            id: 2,
            type: 'expense',
            description: 'Electricity bill payment',
            amount: '$850',
            time: '4 hours ago',
        },
        {
            id: 3,
            type: 'income',
            description: 'Transport fees collected',
            amount: '$1,200',
            time: '6 hours ago',
        },
        {
            id: 4,
            type: 'expense',
            description: 'Office supplies purchase',
            amount: '$320',
            time: '1 day ago',
        },
    ];

    const pendingPayments = [
        { student: 'John Doe', grade: 'Grade 10', amount: '$500', dueDate: '2024-01-15', status: 'overdue' },
        { student: 'Sarah Wilson', grade: 'Grade 9', amount: '$450', dueDate: '2024-01-20', status: 'due' },
        { student: 'Mike Johnson', grade: 'Grade 11', amount: '$600', dueDate: '2024-01-25', status: 'due' },
        { student: 'Emily Brown', grade: 'Grade 12', amount: '$550', dueDate: '2024-01-30', status: 'due' },
    ];

    const monthlyTrend = [
        { month: 'Jan', revenue: 42000, expenses: 28000, profit: 14000 },
        { month: 'Feb', revenue: 43500, expenses: 28500, profit: 15000 },
        { month: 'Mar', revenue: 44200, expenses: 28900, profit: 15300 },
        { month: 'Apr', revenue: 44800, expenses: 29100, profit: 15700 },
        { month: 'May', revenue: 45230, expenses: 28900, profit: 16330 },
    ];

    return (
        <div className="space-y-6">
            {/* Account Dashboard Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold gradient-text">Account Dashboard</h2>
                    <p className="text-gray-600 text-sm">Financial Management Overview</p>
                </div>
                <div className="flex items-center space-x-2 text-xs text-gray-500">
                    <DollarSign className="h-3 w-3" />
                    <span>Financial Management System</span>
                </div>
            </div>

            {/* Financial Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {financialStats.map((stat) => (
                    <Card key={stat.name} glassy={true} className="islamic-pattern group hover:scale-105 transition-transform duration-300">
                        <div className="flex items-center justify-between">
                            <div className="flex-1">
                                <p className="text-sm font-medium text-gray-600 mb-1">{stat.name}</p>
                                <p className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</p>
                                <div className="flex items-center space-x-1">
                                    {stat.trend === 'up' ? (
                                        <TrendingUp className="h-4 w-4 text-green-500" />
                                    ) : (
                                        <TrendingDown className="h-4 w-4 text-red-500" />
                                    )}
                                    <p className={`text-sm font-medium ${stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                                        }`}>
                                        {stat.change} from last month
                                    </p>
                                </div>
                            </div>
                            <div className={`p-4 rounded-2xl ${stat.color} shadow-lg group-hover:shadow-xl transition-shadow duration-300`}>
                                <stat.icon className="h-8 w-8 text-white" />
                            </div>
                        </div>
                    </Card>
                ))}
            </div>

            {/* Account Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {accountMetrics.map((metric) => (
                    <Card key={metric.name} glassy={true} className="islamic-pattern group hover:scale-105 transition-transform duration-300">
                        <div className="flex items-center justify-between">
                            <div className="flex-1">
                                <p className="text-sm font-medium text-gray-600 mb-1">{metric.name}</p>
                                <p className="text-2xl font-bold text-gray-900 mb-2">{metric.value}</p>
                                <div className="flex items-center space-x-1">
                                    {metric.trend === 'up' ? (
                                        <TrendingUp className="h-4 w-4 text-green-500" />
                                    ) : (
                                        <TrendingDown className="h-4 w-4 text-red-500" />
                                    )}
                                    <p className={`text-sm font-medium ${metric.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                                        }`}>
                                        {metric.change} from last month
                                    </p>
                                </div>
                            </div>
                            <div className="p-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                                <metric.icon className="h-8 w-8 text-white" />
                            </div>
                        </div>
                    </Card>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Expense Breakdown */}
                <Card glassy={true} className="islamic-pattern">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900">Expense Breakdown</h3>
                        <div className="p-2 rounded-lg bg-gradient-to-r from-purple-100 to-pink-100">
                            <PieChart className="h-5 w-5 text-purple-600" />
                        </div>
                    </div>

                    <div className="space-y-4">
                        {expenseBreakdown.map((expense, index) => (
                            <div key={index} className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-700">{expense.category}</span>
                                    <span className="text-sm font-bold text-gray-900">${expense.amount.toLocaleString()}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <div className="text-xs text-gray-600 w-16">{expense.percentage}%</div>
                                    <div className="flex-1 bg-gray-200 rounded-full h-3">
                                        <div
                                            className={`${expense.color} h-3 rounded-full`}
                                            style={{ width: `${expense.percentage}%` }}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>

                {/* Monthly Trend */}
                <Card glassy={true} className="islamic-pattern">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900">Monthly Financial Trend</h3>
                        <div className="p-2 rounded-lg bg-gradient-to-r from-teal-100 to-cyan-100">
                            <BarChart3 className="h-5 w-5 text-teal-600" />
                        </div>
                    </div>

                    <div className="space-y-4">
                        {monthlyTrend.map((data, index) => (
                            <div key={index} className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <div className="text-xs text-gray-600 font-medium">{data.month}</div>
                                    <div className="text-xs text-gray-500">Profit: ${data.profit.toLocaleString()}</div>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <div className="text-xs text-gray-600 w-16">Revenue</div>
                                    <div className="flex-1 bg-gray-200 rounded-full h-3">
                                        <div
                                            className="bg-gradient-to-r from-emerald-500 to-teal-500 h-3 rounded-full"
                                            style={{ width: `${(data.revenue / 50000) * 100}%` }}
                                        ></div>
                                    </div>
                                    <div className="text-xs font-medium text-gray-900 w-16">${data.revenue.toLocaleString()}</div>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <div className="text-xs text-gray-600 w-16">Expenses</div>
                                    <div className="flex-1 bg-gray-200 rounded-full h-3">
                                        <div
                                            className="bg-gradient-to-r from-red-500 to-orange-500 h-3 rounded-full"
                                            style={{ width: `${(data.expenses / 30000) * 100}%` }}
                                        ></div>
                                    </div>
                                    <div className="text-xs font-medium text-gray-900 w-16">${data.expenses.toLocaleString()}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent Transactions */}
                <Card glassy={true} className="islamic-pattern">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900">Recent Transactions</h3>
                        <div className="p-2 rounded-lg bg-gradient-to-r from-orange-100 to-red-100">
                            <Clock className="h-5 w-5 text-orange-600" />
                        </div>
                    </div>
                    <div className="space-y-4">
                        {recentTransactions.map((transaction) => (
                            <div key={transaction.id} className="flex items-start space-x-3 p-3 rounded-xl hover:bg-white/20 transition-colors duration-200">
                                <div className="flex-shrink-0">
                                    <div className={`w-3 h-3 rounded-full mt-2 ${transaction.type === 'income' ? 'bg-green-500' : 'bg-red-500'
                                        }`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm text-gray-900 font-medium">{transaction.description}</p>
                                    <div className="flex items-center justify-between mt-1">
                                        <p className="text-xs text-gray-500">{transaction.time}</p>
                                        <p className={`text-sm font-bold ${transaction.type === 'income' ? 'text-green-600' : 'text-red-600'
                                            }`}>
                                            {transaction.type === 'income' ? '+' : '-'}{transaction.amount}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>

                {/* Pending Payments */}
                <Card glassy={true} className="islamic-pattern">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900">Pending Payments</h3>
                        <div className="p-2 rounded-lg bg-gradient-to-r from-red-100 to-orange-100">
                            <AlertCircle className="h-5 w-5 text-red-600" />
                        </div>
                    </div>
                    <div className="space-y-4">
                        {pendingPayments.map((payment, index) => (
                            <div key={index} className="flex items-center justify-between p-3 rounded-xl hover:bg-white/20 transition-colors duration-200">
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900">{payment.student}</p>
                                    <p className="text-xs text-gray-500">{payment.grade} • Due: {payment.dueDate}</p>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <span className="text-sm font-bold text-gray-900">{payment.amount}</span>
                                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${payment.status === 'overdue' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                                        }`}>
                                        {payment.status}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>

            {/* Quick Actions */}
            <Card glassy={true} className="islamic-pattern">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
                    <div className="p-2 rounded-lg bg-gradient-to-r from-emerald-100 to-teal-100">
                        <TrendingUp className="h-5 w-5 text-emerald-600" />
                    </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <a
                        href="/fees/collect"
                        className="p-4 border border-white/20 rounded-xl hover:bg-white/10 transition-all duration-200 backdrop-blur-sm group"
                    >
                        <DollarSign className="h-6 w-6 text-emerald-500 mb-2 group-hover:scale-110 transition-transform" />
                        <p className="text-sm font-medium text-gray-900">Collect Fees</p>
                    </a>
                    <a
                        href="/expenses/add"
                        className="p-4 border border-white/20 rounded-xl hover:bg-white/10 transition-all duration-200 backdrop-blur-sm group"
                    >
                        <Receipt className="h-6 w-6 text-red-500 mb-2 group-hover:scale-110 transition-transform" />
                        <p className="text-sm font-medium text-gray-900">Add Expense</p>
                    </a>
                    <a
                        href="/reports/financial"
                        className="p-4 border border-white/20 rounded-xl hover:bg-white/10 transition-all duration-200 backdrop-blur-sm group"
                    >
                        <FileText className="h-6 w-6 text-blue-500 mb-2 group-hover:scale-110 transition-transform" />
                        <p className="text-sm font-medium text-gray-900">Financial Reports</p>
                    </a>
                    <a
                        href="/budget"
                        className="p-4 border border-white/20 rounded-xl hover:bg-white/10 transition-all duration-200 backdrop-blur-sm group"
                    >
                        <Calculator className="h-6 w-6 text-purple-500 mb-2 group-hover:scale-110 transition-transform" />
                        <p className="text-sm font-medium text-gray-900">Budget Planning</p>
                    </a>
                </div>
            </Card>
        </div>
    );
};

export default DashboardAccount;
